import { adminUploadProfilePictures } from "helpers/backend_helpers";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Alert,
} from "reactstrap";
import SimpleBar from "simplebar-react";

const PhotoUploadModalMember = ({ show, onCloseClick, getData }: any) => {
  const [files, setFiles] = useState<File[]>([]);
  const [detailData, setDetailData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [displayCount, setDisplayCount] = useState(5);

  const uploadData = async () => {
    if (files.length === 0) {
      toast.error("Pilih foto terlebih dahulu");
      return;
    }

    if (files.length > 20) {
      toast.error("Maksimal upload 20 foto");
      return;
    }

    const formData = new FormData();
    for (let index = 0; index < files.length; index++) {
      formData.append("files", files[index]);
    }

    setIsLoading(true);
    await adminUploadProfilePictures(formData)
      .then((response: any) => {
        setDetailData(response);
        getData();

        if (response.countInvalid === 0) {
          toast.success(`Berhasil upload ${response.countValid} foto profil`);
        } else {
          toast.warning(
            `Upload selesai: ${response.countValid} valid, ${response.countInvalid} invalid`
          );
        }
      })
      .catch((error) => {
        toast.error("Gagal upload foto");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);

      if (selectedFiles?.length > 20) {
        toast.warning(
          "Maksimal upload 20 foto. Hanya 20 foto pertama yang akan diproses."
        );
        setFiles(selectedFiles.slice(0, 20));
      } else {
        setFiles(selectedFiles);
      }

      setDetailData(null);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setDetailData(null);
    setDisplayCount(5);
    onCloseClick();
  };

  const handleShowMore = () => {
    setDisplayCount((prev) => prev + 5);
  };

  const getDisplayedData = () => {
    if (!detailData?.notes) return [];
    return detailData.notes.slice(0, displayCount);
  };

  const hasMoreData = () => {
    if (!detailData?.notes) return false;
    return detailData.notes.length > displayCount;
  };

  const removeFile = (indexToRemove: number) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };

  useEffect(() => {
    if (!show) {
      setFiles([]);
      setDetailData(null);
      setDisplayCount(5);
    }
  }, [show]);

  return (
    <Modal
      fade={true}
      isOpen={show}
      toggle={handleClose}
      centered={true}
      size="lg"
    >
      <ModalHeader className="bg-light p-3" toggle={handleClose}>
        Upload Foto Karyawan
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col lg={12}>
            <Alert color="info" className="border-0 mb-3">
              <div className="d-flex align-items-start">
                <i className="ri-information-line fs-5 me-2 mt-1"></i>
                <div>
                  <h6 className="alert-heading mb-2 mt-2">Petunjuk Upload:</h6>
                  <ul className="mb-0 ps-3">
                    <li>
                      Maksimal upload <strong>20 gambar</strong> dalam satu kali
                      proses
                    </li>
                    <li>
                      Format nama file harus sesuai <strong>No Reg</strong>{" "}
                      karyawan (contoh: <code>REG001.jpg</code>)
                    </li>
                    <li>Format gambar yang didukung: JPG, JPEG, PNG</li>
                    <li>Ukuran maksimal per file: 5MB</li>
                  </ul>
                </div>
              </div>
            </Alert>

            <div className="mb-3">
              <Label htmlFor="file-input" className="form-label">
                Pilih Foto
              </Label>
              <Input
                type="file"
                className="form-control"
                id="file-input"
                accept="image/jpeg,image/jpg,image/png"
                multiple
                onChange={handleFileChange}
              />
              <div className="form-text">
                Pilih beberapa file sekaligus (Ctrl/Cmd + Click)
              </div>
            </div>

            {files.length > 0 && !detailData && (
              <Alert color="success" className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>
                    <i className="ri-image-line align-middle me-2"></i>
                    <strong>{files.length}</strong> foto terpilih
                  </span>
                  {files.length > 20 && (
                    <span className="badge bg-warning text-dark">
                      Melebihi batas maksimal!
                    </span>
                  )}
                </div>

                <SimpleBar
                  autoHide={false}
                  style={{ maxHeight: "220px" }}
                  className="px-3 mt-3"
                >
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="d-flex justify-content-between align-items-center py-1 border-bottom"
                    >
                      <span
                        className="text-truncate small"
                        style={{ maxWidth: "80%" }}
                      >
                        <i className="ri-file-image-line me-1"></i>
                        {file.name}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-soft-danger"
                        onClick={() => removeFile(index)}
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  ))}
                </SimpleBar>
              </Alert>
            )}

            {detailData && (
              <div className="mt-3">
                <Alert
                  color={detailData.countInvalid === 0 ? "success" : "warning"}
                  className="border-0"
                >
                  <h5 className="alert-heading mb-3">
                    <i className="ri-information-line align-middle me-2"></i>
                    Hasil Upload
                  </h5>
                  <hr />
                  <Row className="g-3">
                    <Col sm={6}>
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <div className="avatar-sm">
                            <div className="avatar-title bg-success-subtle text-success rounded fs-3">
                              <i className="ri-checkbox-circle-fill"></i>
                            </div>
                          </div>
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <h6 className="mb-1">Foto Valid</h6>
                          <p className="text-muted mb-0 fs-4 fw-semibold">
                            {detailData.countValid || 0}
                          </p>
                        </div>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <div className="avatar-sm">
                            <div className="avatar-title bg-danger-subtle text-danger rounded fs-3">
                              <i className="ri-close-circle-fill"></i>
                            </div>
                          </div>
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <h6 className="mb-1">Foto Invalid</h6>
                          <p className="text-muted mb-0 fs-4 fw-semibold">
                            {detailData.countInvalid || 0}
                          </p>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {detailData.countInvalid > 0 && (
                    <div className="mt-3">
                      <p className="mb-0 text-muted">
                        <i className="ri-information-line align-middle me-1"></i>
                        Terdapat foto yang tidak valid. Periksa detail di bawah.
                      </p>
                    </div>
                  )}
                </Alert>

                {detailData?.notes && detailData?.notes?.length > 0 && (
                  <div className="mt-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h6 className="mb-0">
                        <i className="ri-file-list-3-line align-middle me-2 text-primary"></i>
                        Detail Foto Upload
                      </h6>
                      <span className="badge bg-primary-subtle text-primary">
                        {detailData?.notes?.length} Total File
                      </span>
                    </div>

                    <SimpleBar
                      autoHide={false}
                      style={{ maxHeight: "420px" }}
                      className="px-3"
                    >
                      <div className="list-group list-group-flush">
                        {getDisplayedData().map((item: any, index: number) => {
                          const isValid = !item.note || item.note === "";
                          return (
                            <div
                              key={index}
                              className={`list-group-item list-group-item-action border rounded mb-2 ${
                                isValid
                                  ? "border-success-subtle"
                                  : "border-danger-subtle"
                              }`}
                            >
                              <div className="d-flex w-100 justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center mb-2">
                                    <div
                                      className={`avatar-xs rounded-circle ${
                                        isValid
                                          ? "bg-success-subtle"
                                          : "bg-danger-subtle"
                                      } d-flex align-items-center justify-content-center me-2`}
                                    >
                                      <i
                                        className={`${
                                          isValid
                                            ? "ri-image-line text-success"
                                            : "ri-image-fill text-danger"
                                        } fs-6`}
                                      ></i>
                                    </div>
                                    <div>
                                      <h6
                                        className="mb-0 fw-semibold text-truncate"
                                        style={{ maxWidth: "400px" }}
                                      >
                                        {item.filename}
                                      </h6>
                                      <small className="text-muted">
                                        <i className="ri-file-line align-middle me-1"></i>
                                        File foto profil
                                      </small>
                                    </div>
                                  </div>

                                  {item.note && (
                                    <div className="mt-2 ms-5">
                                      <div className="alert alert-danger py-2 px-3 mb-0 small">
                                        <i className="ri-error-warning-line align-middle me-1"></i>
                                        <strong>Error:</strong> {item.note}
                                      </div>
                                    </div>
                                  )}

                                  {!item.note && (
                                    <div className="mt-2 ms-5">
                                      <p className="mb-0 text-success small">
                                        <i className="ri-checkbox-circle-line align-middle me-1"></i>
                                        Foto berhasil diupload
                                      </p>
                                    </div>
                                  )}
                                </div>

                                <div className="ms-3">
                                  {isValid ? (
                                    <div className="text-center">
                                      <div className="avatar-sm rounded-circle bg-success-subtle d-flex align-items-center justify-content-center">
                                        <i className="ri-checkbox-circle-fill text-success fs-4"></i>
                                      </div>
                                      <small className="text-success fw-medium d-block mt-1">
                                        Valid
                                      </small>
                                    </div>
                                  ) : (
                                    <div className="text-center">
                                      <div className="avatar-sm rounded-circle bg-danger-subtle d-flex align-items-center justify-content-center">
                                        <i className="ri-close-circle-fill text-danger fs-4"></i>
                                      </div>
                                      <small className="text-danger fw-medium d-block mt-1">
                                        Invalid
                                      </small>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {hasMoreData() && (
                        <div className="text-center mt-3">
                          <button
                            type="button"
                            className="btn btn-soft-primary btn-sm waves-effect waves-light"
                            onClick={handleShowMore}
                          >
                            <i className="ri-arrow-down-s-line align-middle me-1"></i>
                            Tampilkan Lebih Banyak
                            <span className="badge bg-primary ms-2">
                              {detailData?.notes?.length - displayCount}
                            </span>
                          </button>
                        </div>
                      )}

                      {!hasMoreData() && detailData?.notes?.length > 5 && (
                        <div className="text-center mt-3">
                          <div className="d-inline-flex align-items-center text-muted">
                            <i className="ri-check-double-line align-middle me-1"></i>
                            <small>
                              Menampilkan semua {detailData?.notes?.length} file
                            </small>
                          </div>
                        </div>
                      )}
                    </SimpleBar>
                  </div>
                )}
              </div>
            )}
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          className="btn btn-soft-danger waves-effect waves-light material-shadow-none btn-sm"
          onClick={handleClose}
          disabled={isLoading}
        >
          {detailData ? "Tutup" : "Batal"}
        </button>
        {!detailData && (
          <button
            type="submit"
            className="btn btn-soft-success waves-effect waves-light material-shadow-none btn-sm"
            onClick={uploadData}
            disabled={files.length === 0 || files.length > 20 || isLoading}
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Uploading...
              </>
            ) : (
              <>
                <i className="ri-upload-2-line align-middle me-1"></i>
                Upload {files.length > 0 && `(${files.length})`}
              </>
            )}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
};

PhotoUploadModalMember.propTypes = {
  onCloseClick: PropTypes.func,
  show: PropTypes.any,
  getData: PropTypes.func,
};

export default PhotoUploadModalMember;
